import React from "react";
import { colors as Colors } from "material-ui/styles";
import { RaisedButton, FlatButton } from "material-ui";

import { formatPrice } from "../../util";

import OrderSeatList from "./order-seat-list.jsx";

const SEATS_IO_SCRIPT_URL = "https://cdn.seatsio.net/chart.js";

class OrderSeatPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showBestAvailableSelector: false,
      editorInstance: null,
      selectedSeats: [],
      badSeats: [],
      droppedSeats: [],
      orderConflictError: false,
      seatBookedError: false
    };

    this._makePricing = this._makePricing.bind(this);
    this.createInstance = this.createInstance.bind(this);
    this.destroyInstance = this.destroyInstance.bind(this);
    this.submit = this.submit.bind(this);
  }

  _makePricing() {
    let { event } = this.props,
      { venue } = event;

    let pricing = {};

    event.ticket_buckets.forEach(b => {
      if (b.reserved_seating_category) {
        if (!pricing[b.reserved_seating_category]) {
          pricing[b.reserved_seating_category] = [];
        }
        pricing[b.reserved_seating_category].push({
          ticketType: b.name,
          price: b.price,
          ticket_bucket: b.id
        });

        if (b.variants) {
          b.variants.forEach(v => {
            pricing[b.reserved_seating_category].push({
              ticketType: `${b.name} – ${v.name}`,
              price: v.price,
              ticket_bucket: b.id,
              variant: v.id
            });
          });
        }
      }
    });

    return Object.keys(pricing).map(key => {
      // Only 1 ticket type for this section
      if (pricing[key].length === 1) {
        return {
          category: key,
          price: pricing[key][0].price,
          ticket_bucket: pricing[key][0].ticket_bucket
        };
      }

      return {
        category: key,
        ticketTypes: pricing[key]
      };
    });
  }

  createInstance() {
    let { event, order } = this.props,
      { venue } = event;

    if (!window.seatsio || !venue || this.state.editorInstance) {
      return;
    }

    let instance = new window.seatsio.SeatingChart({
      divId: "seatioInject",
      publicKey: process.env.SEATS_IO_PUBLIC_KEY,
      event: event.id,
      pricing: this._makePricing(),
      priceFormatter: p => formatPrice(p, /* short */ true),
      priceLevelsTooltipMessage: "Select ticket type:",
      reserveOnSelect: false, // this causes too many problems...
      multiSelectEnabled: true,
      // Specifically gate unavailableCategories to Raising Brows event bucket
      maxSelectedObjects: event && event.event_bucket === "68c8da3e18bf645fd396cd4e" ? null : this.props.quantityAvailable,
      unavailableCategories: event && event.event_bucket === "68c8da3e18bf645fd396cd4e" ? ["Reserved", "Companion Seat", "ADA Space"] : undefined,
      baseColor: "#0288d1",
      selectionValidators: [{ type: "noOrphanSeats", enabled: false }],
      messages: {
        maxSelectionReached: "This seat is not available"
      },
      onObjectSelected: (object, ticketType) => {
        let { selectedSeats } = this.state;

        selectedSeats.push({
          ticket_bucket: event.ticket_buckets.find(
            b =>
              b.reserved_seating_category === object.category.key.toString() &&
              (!ticketType || b.id === ticketType.ticket_bucket)
          ),
          seat: object.label,
          seatmap_object: object,
          variant: ticketType ? ticketType.variant : undefined
        });

        // when you select a new seat, clear the badSeats so the error message goes away if showing
        this.setState({ selectedSeats, badSeats: [] }, () => {
          if (this.seatList) {
            this.seatList.scrollIntoView();
          }
        });
      },
      onObjectDeselected: (object, ticketType) => {
        let { selectedSeats } = this.state;
        selectedSeats.splice(
          this.state.selectedSeats.findIndex(s => s.seat === object.label),
          1
        );
        this.setState({ selectedSeats });
      },
      onSelectedObjectBooked: (object, ticketType) => {
        this.setState({
          badSeats: this.state.badSeats.concat(object.label)
        });
        if (this.props.order.seats_requested) {
          this.setState({ orderConflictError: true });
        }
        else {
          this.setState({ seatBookedError: true });
        }
      },
      onScrolledOutOfBoundsVertically: amount => {
        // This is hacky - basically trasmit the scroll of the seatmap --> overlay,
        // to help for scrolling on mobile.
        let roots = document.querySelectorAll("div > [data-reactroot]");
        roots[roots.length - 1].scrollTop += amount;
      },
      objectIcon: object => {
        if (object.selected) {
          return "check";
        }
        return null;
      }
    });

    this.setState({
      editorInstance: instance
    });

    instance.render();
  }

  destroyInstance() {
    document.getElementById("seatioInject").innerHTML = "";
    window.seatsio.removeFromArray(
      this.state.editorInstance,
      window.seatsio.charts
    );
  }

  async submit() {
    this.setState({ orderConflictError: false, seatBookedError: false });
    let ticketBuckets = this.state.selectedSeats.reduce(function (
      byBucket,
      seat
    ) {
      const key = `${seat.ticket_bucket.id}-${seat.variant || ""}`;
      if (byBucket[key]) {
        byBucket[key].push(seat.seat);
      } else {
        byBucket[key] = [seat.seat];
      }
      return byBucket;
    },
    {});

    this.props.updateCurrentOrder({
      seats_requested: true,
      subitems: [].concat
        .apply(
          [],
          Object.entries(ticketBuckets)
            .sort(([_, seatsA], [__, seatsB]) => {
              // Sort by most tickets to least
              return seatsB.length - seatsA.length;
            })
            .map(([identifier, seats]) => {
              const [id, variant] = identifier.split("-");

              // When applying complex pass benefits, we may need to divide into multiple copies of the
              // same ticket bucket in order to properly span the benefits (e.g. 2 free tix + 15% off the rest,
              // currently the system requires a second ticket bucket to apply the 15% off to).
              if (this.props.order.pass) {
                const ticketBucket = this.props.event.ticket_buckets.find(
                  b => b.id === id
                );
                const passAdjusted =
                  ticketBucket &&
                  ticketBucket.pass_adjusted &&
                  ticketBucket.pass_adjusted[this.props.order.pass];

                if (
                  passAdjusted &&
                  passAdjusted.quantity_available &&
                  passAdjusted.quantity_available < seats.length
                ) {
                  const chunkLength = passAdjusted.quantity_available;
                  return [
                    {
                      ticket_bucket: id,
                      variant: variant,
                      quantity: chunkLength,
                      seats: seats.slice(0, chunkLength)
                    },
                    {
                      ticket_bucket: id,
                      variant: variant,
                      quantity: seats.length - chunkLength,
                      seats: seats.slice(chunkLength)
                    }
                  ];
                }
              }

              return [
                {
                  ticket_bucket: id,
                  variant: variant,
                  quantity: seats.length,
                  seats: seats
                }
              ];
            })
        )
        .concat(
          TENANT.donate_inline_order_prompt ? [{ inline_donation: true }] : []
        )
    });
  }

  componentDidMount() {
    if (!document.querySelector(`script[src="${SEATS_IO_SCRIPT_URL}"]`)) {
      var script = document.createElement("script");
      script.type = "text/javascript";
      script.src = SEATS_IO_SCRIPT_URL;
      script.onload = this.createInstance;
      document.body.appendChild(script);
    }
    this.createInstance();
  }

  componentWillUnmount() {
    if (this.state.editorInstance) {
      this.destroyInstance();
    }
  }

  getStyles() {
    return {
      error: {
        color: "#fff",
        backgroundColor: Colors.red600,
        padding: 10
      },
      inner: {
        width: "100%",
        height: 400,
        position: "relative",
        marginBottom: 16
      },
      innerBestAvailableShown: {
        display: "none"
      },
      actions: {
        marginTop: 20
      }
    };
  }

  render() {

    let styles = this.getStyles(),
      currentBadTickets = [];

    if (this.props.order.seats_requested && this.state.orderConflictError) {
      currentBadTickets = this.state.badSeats.map(s => {
        return { seat: s };
      });
    }
    else if (!this.props.order.seats_requested && this.state.seatBookedError) {
      currentBadTickets = this.state.badSeats.map(s => {
        return { seat: s };
      });
    }

    return (
      <div>
        {currentBadTickets.length ? (
          <div style={styles.error}>
            <div>
              Another customer has reserved the following seat
              {currentBadTickets.length !== 1 ? "s" : ""}:{" "}
              <strong>{currentBadTickets.map(t => t.seat).join(",")}</strong>
            </div>
            <div>
              Please make a different seat selection and proceed to checkout.
            </div>
          </div>
        ) : null}
        {this.state.showBestAvailableSelector ? (
          <div>What are you looking for?</div>
        ) : null}
        <div
          id="seatioInject"
          style={Object.assign(
            {},
            styles.inner,
            this.state.showBestAvailableSelector &&
              styles.innerBestAvailableShown
          )}
        />
        <OrderSeatList
          containerRef={r => {
            this.seatList = r;
          }}
          seats={this.state.selectedSeats}
          event={this.props.event}
          onRemoveSeat={id => this.state.editorInstance.deselectObjects([id])}
        />
        <div style={styles.actions}>
          <FlatButton
            label="Cancel"
            onClick={() => {
              this.props.onCancel();
            }}
            style={{ float: "left" }}
          />
          <RaisedButton
            label={`Continue (${this.state.selectedSeats.length} seat${
              this.state.selectedSeats.length === 1 ? "" : "s"
            })`}
            primary={true}
            disabled={
              this.state.selectedSeats.length === 0
            }
            onClick={this.submit}
            style={{ float: "right" }}
          />
        </div>
      </div>
    );
  }
}

export default OrderSeatPicker;



// WEBPACK FOOTER //
// ./src/app/components/partials/order-seat-picker.jsx